import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import theme from './theme';
import Login from "./pages/Login";
import Landing from "./pages/Landing";
import Dashboard from "./pages/Dashboard";
import Products from "./pages/Products";
import Customers from "./pages/Customers";
import StockMovements from "./pages/StockMovements";
import PDV from "./pages/PDV";
import Sales from "./pages/Sales";
import Cashier from "./pages/Cashier";
import Purchases from "./pages/Purchases";
import Finance from "./pages/Finance";
import Reports from "./pages/Reports";
import Users from "./pages/Users";
import Layout from "./components/Layout";
import PaymentBlocked from "./pages/PaymentBlocked";
import AdminDashboard from "./pages/admin/AdminDashboard";
import CompanyList from "./pages/admin/CompanyList";
import CompanyForm from "./pages/admin/CompanyForm";
import CompanyDetails from "./pages/admin/CompanyDetails";
import CompanyEdit from "./pages/admin/CompanyEdit";
import CompanyUsers from "./pages/admin/CompanyUsers";
import AdminReports from "./pages/admin/AdminReports";
import AdminLayout from "./components/AdminLayout";
import TrialBanner from "./components/TrialBanner";
import { Toaster } from "./components/ui/sonner";
import Suppliers from "./pages/Suppliers";
import Categories from "./pages/Categories";
import SalesSupport from "./pages/admin/SalesSupport";
import AdminSettings from "./pages/admin/AdminSettings";
// Novas telas
import FiscalDocuments from "./pages/FiscalDocuments";
import DigitalCertificates from "./pages/DigitalCertificates";
import RecurringCharges from "./pages/RecurringCharges";
import BankReconciliation from "./pages/BankReconciliation";
import WhatsAppIntegration from "./pages/WhatsAppIntegration";
import DFeSearch from "./pages/DFeSearch";
import XmlImport from "./pages/XmlImport";
import SystemSettings from "./pages/SystemSettings";
import CompanyProfile from "./pages/CompanyProfile";
import Billing from "./pages/Billing";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Setup axios interceptor
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const response = await axios.get(`${API}/auth/me`);
        setUser(response.data);
        setIsAuthenticated(true);
      } catch (error) {
        localStorage.removeItem("token");
        setIsAuthenticated(false);
      }
    }
    setLoading(false);
  };

  const handleLogin = (token, userData, blocked = false, companyData = null) => {
    if (blocked) {
      // Se está bloqueado, armazenar info mas não autenticar
      setUser({ ...userData, company: companyData, blocked: true });
      setIsAuthenticated(false);
      return;
    }
    localStorage.setItem("token", token);
    setUser(userData);
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    setUser(null);
    setIsAuthenticated(false);
  };

  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <div
          data-testid="splash-screen"
          style={{
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: "20px",
            background:
              "radial-gradient(circle at 20% 20%, rgba(14,165,233,0.15), transparent 60%), radial-gradient(circle at 80% 80%, rgba(56,189,248,0.10), transparent 60%), #0f172a",
          }}
        >
          <img
            src={`${process.env.PUBLIC_URL || ""}/brand/icon.png`}
            alt="Stock Sistema"
            style={{
              width: 96,
              height: 96,
              animation: "pulse 1.6s ease-in-out infinite",
              filter: "drop-shadow(0 8px 24px rgba(14,165,233,0.45))",
            }}
          />
          <div
            style={{
              color: "#ffffff",
              fontFamily: "Inter, system-ui, sans-serif",
              fontSize: "1.4rem",
              fontWeight: 700,
              letterSpacing: "-0.01em",
            }}
          >
            Stock Sistema
          </div>
          <div
            style={{
              color: "#94a3b8",
              fontFamily: "Inter, system-ui, sans-serif",
              fontSize: "0.85rem",
              letterSpacing: "0.5px",
              textTransform: "uppercase",
            }}
          >
            Carregando...
          </div>
          <style>{`
            @keyframes pulse {
              0%, 100% { transform: scale(1); opacity: 1; }
              50% { transform: scale(1.06); opacity: 0.85; }
            }
          `}</style>
        </div>
      </ThemeProvider>
    );
  }

  // Verificar se usuário está bloqueado
  if (user?.blocked && user?.company) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <div className="App">
          <Toaster position="top-right" richColors />
          <PaymentBlocked company={user.company} onLogout={handleLogout} />
        </div>
      </ThemeProvider>
    );
  }

  // Verificar se é system_admin e redirecionar para painel admin
  const isSystemAdmin = user?.role === 'system_admin';

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <div className="App">
        <Toaster position="top-right" richColors />
        <BrowserRouter>
        <Routes>
          <Route
            path="/login"
            element={
              !isAuthenticated ? (
                <Login onLogin={handleLogin} />
              ) : isSystemAdmin ? (
                <Navigate to="/admin" replace />
              ) : (
                <Navigate to="/dashboard" replace />
              )
            }
          />

          {/* Public Landing Page */}
          <Route path="/" element={<Landing />} />
          
          {/* Admin Routes (system_admin only) */}
          <Route
            path="/admin"
            element={
              isAuthenticated && isSystemAdmin ? (
                <AdminLayout user={user} onLogout={handleLogout}>
                  <AdminDashboard />
                </AdminLayout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/admin/companies"
            element={
              isAuthenticated && isSystemAdmin ? (
                <AdminLayout user={user} onLogout={handleLogout}>
                  <CompanyList />
                </AdminLayout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/admin/companies/new"
            element={
              isAuthenticated && isSystemAdmin ? (
                <AdminLayout user={user} onLogout={handleLogout}>
                  <CompanyForm />
                </AdminLayout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/admin/companies/:id"
            element={
              isAuthenticated && isSystemAdmin ? (
                <AdminLayout user={user} onLogout={handleLogout}>
                  <CompanyDetails />
                </AdminLayout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/admin/companies/:id/edit"
            element={
              isAuthenticated && isSystemAdmin ? (
                <AdminLayout user={user} onLogout={handleLogout}>
                  <CompanyEdit />
                </AdminLayout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/admin/companies/:id/users"
            element={
              isAuthenticated && isSystemAdmin ? (
                <AdminLayout user={user} onLogout={handleLogout}>
                  <CompanyUsers />
                </AdminLayout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/admin/reports"
            element={
              isAuthenticated && isSystemAdmin ? (
                <AdminLayout user={user} onLogout={handleLogout}>
                  <AdminReports />
                </AdminLayout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/admin/sales-support"
            element={
              isAuthenticated && isSystemAdmin ? (
                <AdminLayout user={user} onLogout={handleLogout}>
                  <SalesSupport />
                </AdminLayout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/admin/settings"
            element={
              isAuthenticated && isSystemAdmin ? (
                <AdminLayout user={user} onLogout={handleLogout}>
                  <AdminSettings />
                </AdminLayout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          {/* Company Routes (NOT for system_admin) */}
          <Route
            path="/dashboard"
            element={
              isAuthenticated && !isSystemAdmin ? (
                <Layout user={user} onLogout={handleLogout}>
                  {user?.trial_warning && (
                    <TrialBanner trialInfo={user.trial_warning} companyId={user.company_id} />
                  )}
                  <Dashboard />
                </Layout>
              ) : isSystemAdmin ? (
                <Navigate to="/admin" replace />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/products"
            element={
              isAuthenticated ? (
                <Layout user={user} onLogout={handleLogout}>
                  <Products />
                </Layout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/customers"
            element={
              isAuthenticated ? (
                <Layout user={user} onLogout={handleLogout}>
                  <Customers />
                </Layout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/stock-movements"
            element={
              isAuthenticated ? (
                <Layout user={user} onLogout={handleLogout}>
                  <StockMovements />
                </Layout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/pdv"
            element={
              isAuthenticated ? (
                <Layout user={user} onLogout={handleLogout}>
                  <PDV />
                </Layout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/sales"
            element={
              isAuthenticated ? (
                <Layout user={user} onLogout={handleLogout}>
                  <Sales />
                </Layout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/suppliers"
            element={
              isAuthenticated ? (
                <Layout user={user} onLogout={handleLogout}>
                  <Suppliers user={user}/>
                </Layout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/categories"
            element={
              isAuthenticated ? (
                <Layout user={user} onLogout={handleLogout}>
                  <Categories user={user}/>
                </Layout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/cashier"
            element={
              isAuthenticated ? (
                <Layout user={user} onLogout={handleLogout}>
                  <Cashier user={user}/>
                </Layout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/purchases"
            element={
              isAuthenticated ? (
                <Layout user={user} onLogout={handleLogout}>
                  <Purchases />
                </Layout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/finance"
            element={
              isAuthenticated ? (
                <Layout user={user} onLogout={handleLogout}>
                  <Finance />
                </Layout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/billing"
            element={
              isAuthenticated && !isSystemAdmin ? (
                <Layout user={user} onLogout={handleLogout}>
                  <Billing user={user} />
                </Layout>
              ) : isSystemAdmin ? (
                <Navigate to="/admin" replace />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/reports"
            element={
              isAuthenticated ? (
                <Layout user={user} onLogout={handleLogout}>
                  <Reports />
                </Layout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/users"
            element={
              isAuthenticated ? (
                <Layout user={user} onLogout={handleLogout}>
                  <Users />
                </Layout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          {/* Novas Rotas - Notas Fiscais */}
          <Route
            path="/fiscal"
            element={
              isAuthenticated ? (
                <Layout user={user} onLogout={handleLogout}>
                  <FiscalDocuments />
                </Layout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/certificates"
            element={
              isAuthenticated ? (
                <Layout user={user} onLogout={handleLogout}>
                  <DigitalCertificates />
                </Layout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/recurring"
            element={
              isAuthenticated ? (
                <Layout user={user} onLogout={handleLogout}>
                  <RecurringCharges />
                </Layout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/bank"
            element={
              isAuthenticated ? (
                <Layout user={user} onLogout={handleLogout}>
                  <BankReconciliation />
                </Layout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/whatsapp"
            element={
              isAuthenticated ? (
                <Layout user={user} onLogout={handleLogout}>
                  <WhatsAppIntegration />
                </Layout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/dfe"
            element={
              isAuthenticated ? (
                <Layout user={user} onLogout={handleLogout}>
                  <DFeSearch />
                </Layout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/xml-import"
            element={
              isAuthenticated ? (
                <Layout user={user} onLogout={handleLogout}>
                  <XmlImport />
                </Layout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/settings"
            element={
              isAuthenticated ? (
                <Layout user={user} onLogout={handleLogout}>
                  <SystemSettings />
                </Layout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/company-profile"
            element={
              isAuthenticated && !isSystemAdmin ? (
                <Layout user={user} onLogout={handleLogout}>
                  <CompanyProfile />
                </Layout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
        </Routes>
      </BrowserRouter>
    </div>
    </ThemeProvider>
  );
}

export default App;
